import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTable, usePagination } from "react-table";
import { UserPlus, X } from "lucide-react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Nav from "../Components/Nav/Nav";
import Login from "./Login";
import AdminMenu from "../Components/Admin/AdminMenu";
import AdminFormHeader from "../Components/Admin/AdminFormHeader";
import KnowledgeMenu from "../Components/Knowledge/knowledgeMenu";
import { getRequestKnowledge } from "../actions/requestKnowledge";
import { DateParser } from "../Components/utils/Utils";
import AsignKnowledge from "../Components/Knowledge/AssigneKnowledge";
import ImageProfil from "../Components/Profil/ImageProfil";

const AdminKnowledge = () => {
  const userData = useSelector((state) => state.userReducer);
  const knowledgeRequest = useSelector((state) => state.requestKnowledgeReducer);
  const dispatch = useDispatch();

  const [selectedRequest, setSelectedRequest] = useState(null);

  useEffect(() => {
    dispatch(getRequestKnowledge());
  }, [dispatch]);

  // L'inbox "À traiter" ne liste que les demandes non encore assignées (statut "0").
  const data = useMemo(
    () =>
      Object.values(knowledgeRequest).filter(
        (request) => request.requestStatus === "0"
      ),
    [knowledgeRequest]
  );

  const columns = useMemo(
    () => [
      {
        Header: "Demandeur",
        accessor: "user",
        Cell: ({ row }) => (
          <div className="req-cell-user">
            <ImageProfil id={row?.original?.user?.id} />
            <span>
              {row?.original?.user?.firstname} {row?.original?.user?.name}
            </span>
          </div>
        ),
      },
      {
        Header: "Nature",
        accessor: "nature",
        Cell: ({ value }) => <span className="req-chip">{value}</span>,
      },
      { Header: "Catégorie", accessor: "categorie" },
      {
        Header: "Date",
        accessor: "createdAt",
        Cell: ({ value }) => DateParser(value),
      },
      {
        Header: "Action",
        accessor: "action",
        Cell: ({ row }) => (
          <button
            className="ds-btn ds-btn--primary"
            onClick={() => setSelectedRequest(row.original)}
          >
            <UserPlus size={14} />
            Assigner
          </button>
        ),
      },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    state,
    gotoPage,
    setPageSize,
    prepareRow,
  } = useTable(
    { columns, data, initialState: { pageIndex: 0, pageSize: 5 } },
    usePagination
  );

  const { pageIndex, pageSize } = state;

  return (
    <>
      <ToastContainer />
      {userData?.isAdmin || userData?.isReferent ? (
        <>
          <Nav />
          <div className="mainContainer">
            <AdminMenu />
            <div className="centerBlock_admin">
              <AdminFormHeader
                title="Demandes de tutoriels"
                subtitle="Bac à sable des demandes à assigner à un référent."
              />
              <KnowledgeMenu active="requestKnowledge" />

              {data.length === 0 ? (
                <div className="req-empty">
                  Aucune demande à traiter pour le moment.
                </div>
              ) : (
                <>
                  <div className="admin-table-wrap">
                    <table {...getTableProps()}>
                      <thead>
                        {headerGroups.map((headerGroup) => (
                          <tr
                            {...headerGroup.getHeaderGroupProps()}
                            key={headerGroup.id}
                          >
                            {headerGroup.headers.map((column) => (
                              <th {...column.getHeaderProps()} key={column.id}>
                                {column.render("Header")}
                              </th>
                            ))}
                          </tr>
                        ))}
                      </thead>
                      <tbody {...getTableBodyProps()}>
                        {page.map((row) => {
                          prepareRow(row);
                          return (
                            <tr {...row.getRowProps()} key={row.id}>
                              {row.cells.map((cell) => (
                                <td
                                  {...cell.getCellProps()}
                                  key={cell.column.id}
                                >
                                  {cell.render("Cell")}
                                </td>
                              ))}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="pagination">
                    <button
                      onClick={() => previousPage()}
                      disabled={!canPreviousPage}
                    >
                      Précédent
                    </button>
                    <button onClick={() => nextPage()} disabled={!canNextPage}>
                      Suivant
                    </button>
                    <span>
                      Page <strong>{pageIndex + 1}</strong> sur{" "}
                      <strong>{pageOptions.length}</strong>
                    </span>
                    <span>
                      Aller à la page :{" "}
                      <input
                        type="number"
                        defaultValue={pageIndex + 1}
                        onChange={(e) => {
                          const pageNumber = e.target.value
                            ? Number(e.target.value) - 1
                            : 0;
                          gotoPage(pageNumber);
                        }}
                        style={{ width: "50px" }}
                      />
                    </span>{" "}
                    <select
                      value={pageSize}
                      onChange={(e) => setPageSize(Number(e.target.value))}
                    >
                      {[5, 10, 20].map((size) => (
                        <option key={size} value={size}>
                          Afficher {size}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              {selectedRequest && (
                <div className="modal">
                  <div className="modal-content req-modal">
                    <div className="req-modal-head">
                      <h2>Détails de la demande</h2>
                      <button
                        className="req-modal-close"
                        type="button"
                        onClick={() => setSelectedRequest(null)}
                        aria-label="Fermer"
                      >
                        <X size={18} />
                      </button>
                    </div>
                    <AsignKnowledge
                      request={selectedRequest}
                      closeModal={() => setSelectedRequest(null)}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        <Login />
      )}
    </>
  );
};

export default AdminKnowledge;
